import styled, { css } from "styled-components";
import { mediaQueries, spacing, typographyMixin } from "@magicbi/ui-library";

export const Root = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100%;
  min-width: 0;
  background: ${({ theme }) => theme.colors.surface.secondary};

  /* Below lg the preview wraps under the work, so the page scrolls as one (the page wrapper scrolls). */
  ${mediaQueries.down.lg} {
    height: auto;
    min-height: 100%;
  }
`;

export const TopBar = styled.header`
  display: flex;
  align-items: center;
  flex: none;
  flex-wrap: wrap;
  gap: ${spacing[2]};
  min-height: ${spacing[14]};
  padding: 0 ${spacing[7]};
  background: ${({ theme }) => theme.colors.surface.primary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.stroke};

  ${mediaQueries.down.md} {
    padding: ${spacing[2]} ${spacing[4]};
  }
`;

/** A banner under the top bar; the Columns' side gutter keeps it in line with the page. */
export const Banner = styled.div`
  flex: none;
  padding: ${spacing[4]} ${spacing[8]} 0;
  min-width: 0;

  ${mediaQueries.down.md} {
    padding: ${spacing[3]} ${spacing[4]} 0;
  }
`;

export const Heading = styled.div`
  flex: none;
  padding: ${spacing[5]} ${spacing[8]} ${spacing[1]};
  min-width: 0;

  ${mediaQueries.down.md} {
    padding: ${spacing[4]} ${spacing[4]} 0;
  }
`;

/** From lg up the three panels scroll on their own inside this row; below lg the page scrolls as one. */
export const Columns = styled.div<{ $wideWork?: boolean }>`
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: ${({ $wideWork }) => ($wideWork ? "13rem minmax(0, 1fr)" : "13rem minmax(0, 1fr) 25rem")};
  grid-template-rows: minmax(0, 1fr);
  gap: ${spacing[8]};
  align-items: stretch;
  padding: 0 ${spacing[8]};

  ${mediaQueries.down.lg} {
    grid-template-columns: 13rem minmax(0, 1fr);
    grid-template-rows: none;
    align-items: start;
    gap: ${spacing[6]};
    padding: ${spacing[5]} ${spacing[8]} ${spacing[8]};
  }

  ${mediaQueries.down.md} {
    grid-template-columns: minmax(0, 1fr);
    gap: ${spacing[4]};
    padding: ${spacing[4]} ${spacing[4]} ${spacing[6]};
  }
`;

/** Shared by the three panels: own scroll, padding inside the scroller, scroll never chains to the page. */
const panelScroll = css`
  min-width: 0;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: ${spacing[5]} 0 ${spacing[8]};

  ${mediaQueries.down.lg} {
    overflow-y: visible;
    padding: 0;
  }
`;

export const Steps = styled.nav`
  ${panelScroll}

  ${mediaQueries.down.lg} {
    position: sticky;
    top: ${spacing[4]};
  }

  ${mediaQueries.down.md} {
    position: static;
  }
`;

export const Work = styled.main`
  ${panelScroll}
`;

export const Preview = styled.aside`
  ${panelScroll}

  ${mediaQueries.down.lg} {
    grid-column: 1 / -1;
  }
`;

export const Footer = styled.footer`
  position: sticky;
  bottom: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${spacing[3]};
  padding: 0.875rem ${spacing[8]}; /* GAP: 14px vertical, no spacing token */
  ${typographyMixin("caption", 3)}
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ theme }) => theme.colors.surface.primary};
  border-top: 1px solid ${({ theme }) => theme.colors.border.stroke};

  ${mediaQueries.down.md} {
    padding: ${spacing[3]} ${spacing[4]};
  }
`;
